import { Cluster, MarkerClusterer, SuperClusterAlgorithm } from "@googlemaps/markerclusterer";
import { t } from "@ebay/i18n-ebay";
import { debounce } from "es-toolkit/function";
import WatchService from "@ebay-business-components/favorites-utils/dist/services/watch-service-provider";
import { setAttrsForClickTracking } from "../../utils/tracking";
import { trackingDataCluster, trackingDataClusterStacked } from "../map-cluster/tracking";
import type { PointOfInterest, RequestedListing } from "../../@types/mapviewxs";
import { getPreviewCardPosition } from "../../utils/preview-card-position";
import { focusOnSelectedMarkerPreview } from "../../utils/accessibility";
import { closeMapHelpTooltip } from "../map-help/utils";
import { extractCountryCodeFromMarketplaceId } from "../../utils/format";
import { isProd } from "../../utils/env";
import { validateZipcode } from "../../utils/validation";
import {
  areMarkersInSameCoordinates,
  calculateMinMaxLatitude,
  calculateRadiusForVisibleRegion,
  extractPosition,
  getVisiblePointsOfInterest,
  isSameBound,
  positionToLiteral,
} from "./helpers";
import {
  findMarkerIndex,
  highlightMarker,
  renderMarker,
  renderUserLocationMarker,
  selectMarker,
  toggleWalkingDistanceTourtip,
  unHighlightMarkers,
  unselectMarkers,
  updateWalkingDistanceOverlayRadius,
} from "./marker";
import { createMap } from "./map";
import { loadLibraries } from "./libraries";
import { drawCircle } from "./circle";
import { clusterRenderer, createActiveClusterComponent, createClusterComponent, createClusterItems, findClusterGroup } from "./cluster";
import {
  type AvailableFilter,
  type AvailableFilterOption,
  type FilterKey,
  type Input,
  type LatLng,
  type LatLngBounds,
  type LoadLocationDataParams,
  MapDialog,
  type MapViewModules,
  type MarkerSelectionState,
  type MarkerWithCircle,
  MessageName,
  type State,
  type UserLocationMarker,
} from "./types";
import {
  extractAvailableFilters,
  getAvailableFiltersToDisplay,
  getFilteredItems,
  hasActiveFilters,
  onFilterDialogStyleClear,
  resetFilters,
  updateFilter,
} from "./helpers/filter";
import {
  ACTIVE_MARKER_Z_INDEX,
  DEFAULT_CLUSTER_RADIUS,
  DEFAULT_DISTANCE,
  DEFAULT_EXTENT,
  DEFAULT_MAP_PADDING,
  MARKER_Z_INDEX,
  MAX_ZOOM_LEVEL,
  MIN_DESKTOP_WIDTH,
  SCROLL_TO_MARKER_DEBOUNCE_MS,
  SHOW_CIRCLES_ZOOM_LEVEL,
  SHOW_WALKING_DISTANCE_CIRCLE_ZOOM_LEVEL,
} from "./const";
import { getDistance, getSources, getZipCode, loadDataForLocation, loadListings, loadMore } from "./loadmore";
import { onMessage, sendMapInitToParentWindow, sendModelToParentWindow, sendUpdatedLocationToParentWindow } from "./post-message";
import { getMarkersBounds, moveMapToBounds } from "./helpers/bounds";
import { getPositionToCheck, scrollToMarker } from "./helpers/scrollToMarker";
import { initialState, stateFromModel } from "./helpers/state";
import { centerUserLocation, isDistanceInHyperLocalRange, restorePreviousLocation } from "./location";
import { getLatLngFromAddress, getZipCodeFromLatLng } from "./geocoding";
import { enrichWatchlistItems, getWatchListItemState, getWatchlistClusterItems, toggleWatchlistItems } from "./helpers/watchlist";
export default class extends Marko.Component<Input, State> {
  declare mapsLibrary: google.maps.MapsLibrary | undefined;
  declare markerLibrary: google.maps.MarkerLibrary | undefined;
  declare map: google.maps.Map | undefined;
  declare mapEl: HTMLElement | undefined;
  declare points: MarkerWithCircle[];
  declare markerCluster: MarkerClusterer | null;
  declare currentBounds: LatLngBounds | undefined;
  declare currentZoom: number | undefined;
  declare minLatitude: number | null;
  declare maxLatitude: number | null;
  declare selectedCluster: Cluster | null;
  declare highlightedCluster: Cluster | null;
  declare selectedMarkerWithPreview: MarkerWithCircle | null;
  declare highlightedMarkerFromCluster: MarkerWithCircle | null;
  declare isFocused: boolean;
  declare userLocationMarker: UserLocationMarker | null;
  declare walkingDistanceBounds: LatLngBounds | null;
  declare walkingDistanceOverlay: google.maps.OverlayView | null;
  declare hyperLocalItems: number[];
  declare watchInstance: WatchService | null | undefined;
  declare watchedSet: Set<string>;
  declare isProgramaticallyChangingBounds: boolean;
  declare previousZipcode: string | undefined;
  declare previousZoom: number | undefined;
  declare previousDistance: string | undefined;
  declare scrollToMarkerTimeout: NodeJS.Timeout | null;
  declare loadMoreVersion: number;

  onCreate({ model, isMobile, locality, watchInstance }: Input) {
    this.state = {
      ...initialState,
      ...stateFromModel(model),
      windowWidth: isMobile ? 0 : MIN_DESKTOP_WIDTH,
      userLocationCoordinates: null,
      showLocateMeButton: true,
      shouldOpenMapHelp: undefined,
      locality,
      isLocationChangeDialogOpen: false,
      isFilterConditionDialogOpen: false,
      isFilterPriceDialogOpen: false,
      userLocationZipcode: undefined,
      expandedView: "list",
      isWatchlistPillActive: false,
      showPostcodeInquiry: false,
    } as State;
    this.mapsLibrary = undefined;
    this.markerLibrary = undefined;
    this.map = undefined;
    this.mapEl = undefined;
    this.points = [];
    this.markerCluster = null;
    this.currentBounds = undefined;
    this.currentZoom = undefined;
    this.minLatitude = null;
    this.maxLatitude = null;
    this.selectedCluster = null;
    this.highlightedCluster = null;
    this.selectedMarkerWithPreview = null;
    this.highlightedMarkerFromCluster = null;
    this.scrollToMarkerTimeout = null;
    this.isFocused = false;
    this.userLocationMarker = null;
    this.walkingDistanceBounds = null;
    this.walkingDistanceOverlay = null;
    this.hyperLocalItems = [];
    this.isProgramaticallyChangingBounds = false;
    this.loadMoreVersion = 0;
    this.watchInstance = watchInstance;
  }

  toggleExpandedView() {
    this.state.expandedView = this.state.expandedView === "list" ? "map" : "list";
    window.scrollTo(0, 0);
  }

  handleWindowResize() {
    if (window?.innerWidth) {
      this.state.isPreviewCardEnabled = Boolean(
        this.input.isPreviewCardEnabled && ["left-rail", "feed"].includes(this.input.listingsView || "") && window.innerWidth >= MIN_DESKTOP_WIDTH,
      );
      this.state.windowWidth = window.innerWidth;
      this.clearHighlightedMarkers();
      this.clearSelectedMarkers();
    }
  }

  async onMount() {
    const { mapsLibrary, markerLibrary } = await loadLibraries(this.input.apiKey);
    this.mapsLibrary = mapsLibrary;
    this.markerLibrary = markerLibrary;

    const { model, isRecommendationsView, dialogTitle, dialogSubtitle, favoritesSettings } = this.input || {};

    this.watchInstance = new WatchService({
      hostname: favoritesSettings?.hostname as string,
      addToWatchCSRFToken: favoritesSettings?.csrfTokensForFavorite.addToWatchCSRFToken || "",
      removeFromWatchCSRFToken: favoritesSettings?.csrfTokensForFavorite.removeFromWatchCSRFToken || "",
    });

    // Check if the page is rendered inside an iframe
    if (window !== window.top) {
      // Page is loaded in iframe
      if (isRecommendationsView) {
        this.onRecommendationsView();
        return;
      }

      const defaultIntitialModel = {
        mapview: {
          title: { textSpans: [{ text: dialogTitle }] },
          subtitle: { textSpans: [{ text: dialogSubtitle }] },
        },
      };

      sendModelToParentWindow(model || defaultIntitialModel);
      sendMapInitToParentWindow();
      this.initSetListingsHandler();
      this.initOpenLocationChangeDialogHandler();
    } else {
      this.onRecommendationsView();
    }

    this.subscribeTo(window).on("resize", debounce(this.handleWindowResize.bind(this), 200));
    this.handleWindowResize();
  }

  async getWatchListPOIs() {
    if (!this.input.watchListItems?.length) return;
    try {
      const listingsResult = await loadListings({
        listings: this.input.watchListItems.map(({ item }) => ({ id: item.listing.listingId })),
        csrfToken: this.input.csrfToken,
      });
      this.state.watchListItems = enrichWatchlistItems({
        watchlistItems: this.input.watchListItems,
        watchlistPointsOfInterest: listingsResult?.mapview?.pointsOfInterest,
      });
    } catch {
      console.error("Error fetching watchlist POIs");
    }
  }

  async onWatchlistPillClick() {
    const { signInPageUrl } = this.input;
    if (signInPageUrl) {
      // Check if the page is rendered inside an iframe
      if (window !== window.top) {
        return window.top?.location.assign(signInPageUrl);
      } else {
        return window.location.assign(signInPageUrl);
      }
    }

    const isSelected = !this.state.isWatchlistPillActive;

    if (isSelected) {
      await this.enterWatchlistView();
    } else {
      await this.exitWatchlistView();
    }

    this.state.isWatchlistPillActive = isSelected;
  }

  async enterWatchlistView() {
    this.previousZipcode = getZipCode();
    this.previousZoom = this.map?.getZoom();
    this.previousDistance = getDistance();
    this.state.isLoading = true;
    this.clearMap();
    const watchlistPOIs = this.state.watchListItems.map(({ item }) => item.listing.poi).filter(Boolean) as PointOfInterest[];
    this.state.pointsOfInterest = watchlistPOIs;
    this.state.availableFilters = extractAvailableFilters(watchlistPOIs);
    this.addMarkersAndClusters();

    // Add delay to ensure state change is detected by child components
    setTimeout(() => {
      this.state.isLoading = false;
    }, 0);
  }

  refreshWatchlistViewState() {
    const watchlistPOIs = this.state.watchListItems.map(({ item }) => item.listing.poi).filter(Boolean) as PointOfInterest[];
    this.state.pointsOfInterest = watchlistPOIs;
    this.state.availableFilters = getAvailableFiltersToDisplay(watchlistPOIs, this.state.availableFilters);

    if (hasActiveFilters(this.state.availableFilters)) {
      this.applyFilters();
    }

    if (this.selectedCluster) {
      this.state.clusterPointsOfInterest = getWatchlistClusterItems({
        clusterMarkers: this.selectedCluster.markers,
        pointsOfInterest: this.state.pointsOfInterest,
        watchListItems: this.state.watchListItems,
      });
    }
  }

  async exitWatchlistView() {
    this.clearAllFilters();

    if (this.previousZipcode && this.map) {
      const map = this.map;
      const previousZipcode = this.previousZipcode;
      const previousDistance = this.previousDistance;

      this.isProgramaticallyChangingBounds = true;

      try {
        await restorePreviousLocation({
          map,
          apiKey: this.input.apiKey,
          marketplaceId: this.input.marketplaceId,
          previousZipcode,
          previousZoom: this.previousZoom,
          previousDistance,
          setProgramaticallyChangingBounds: (value) => {
            this.isProgramaticallyChangingBounds = value;
          },
        });

        this.state.isLoading = true;
        this.state.isLoadingMoreItems = true;
        const modules = await loadDataForLocation({
          zipcode: previousZipcode,
          distance: previousDistance ?? getDistance(),
        });
        this.onLoadMoreSuccessHandler({ model: modules, method: "get" }, false);
      } catch (error: any) {
        this.handleLoadMoreError();
        console.error("Failed to load data after exiting watchlist view", error);
      } finally {
        this.state.isLoading = false;
        this.state.isLoadingMoreItems = false;
        this.isProgramaticallyChangingBounds = false;
      }
    } else {
      await this.onLoadMoreHandler();
    }
  }

  validateZipcodeForMarketplace(zipcode: string): boolean {
    const validation = this.input.model?.mapParamsGathering?.locationParams?.postalCodeEntry?.validation;
    if (validation) {
      const isValid = validateZipcode(zipcode, { validation });
      return isValid;
    }
    return true;
  }

  async loadMapForUserZipcode() {
    try {
      this.state.isLoadingMoreItems = true;
      this.isProgramaticallyChangingBounds = true;
      await this.showUserLocation();
      await this.setUserZipCode();
      if (!this.state.userLocationZipcode) throw new Error("Unable to determine user zipcode");

      const isValid = this.validateZipcodeForMarketplace(this.state.userLocationZipcode);
      if (!isValid) {
        throw new Error(`Zipcode ${this.state.userLocationZipcode} is not valid for current marketplace`);
      }

      const modules = await loadDataForLocation({
        zipcode: this.state.userLocationZipcode,
        distance: getDistance() || DEFAULT_DISTANCE,
        sources: getSources(),
      });
      this.onLoadMoreSuccessHandler({ model: modules, method: "get" }, false);
    } catch (error) {
      const urlParams = new URLSearchParams(window.location.search);
      const isMock = !isProd() && urlParams.has("mock");
      if (!isMock) {
        this.state.showPostcodeInquiry = true;
      }
      console.error("Failed to load map for user zipcode", error);
    } finally {
      this.state.isLoadingMoreItems = false;
      this.isProgramaticallyChangingBounds = false;
    }
  }

  async validateZipCodeForRecommendation(): Promise<LatLng | null> {
    const zipcode = this.state.userLocationZipcode || getZipCode();
    if (!zipcode) return null;

    const isValid = this.validateZipcodeForMarketplace(zipcode);
    if (!isValid) return null;

    const country = extractCountryCodeFromMarketplaceId(this.input.marketplaceId);
    try {
      return await getLatLngFromAddress(this.input.apiKey, `${zipcode}, ${country}`);
    } catch (error) {
      console.error("Invalid zipcode for country", error);
      return null;
    }
  }

  // Recommendations flow
  async onRecommendationsView() {
    await this.initMap();

    const hasMapParamsGathering = !!this.input.model?.mapParamsGathering;
    if (hasMapParamsGathering) {
      const coordinates = await this.validateZipCodeForRecommendation();
      this.state.showPostcodeInquiry = !coordinates;
    }

    this.state.isLoading = false;

    if (this.input?.waitingForService || this.state.showPostcodeInquiry) return;

    this.addMarkersAndClusters();
  }

  async initMap() {
    this.mapEl = this.getEl("map");

    const { mapId, isMobile, locality } = this.input;

    const map = createMap({
      mapId,
      mapsLibrary: this.mapsLibrary!,
      mapEl: this.mapEl,
      isMobile,
      locality,
      marketplaceId: this.input.marketplaceId,
    });

    if (!map) throw Error("Map failed to load");

    map.addListener("click", this.onMapClick.bind(this));
    map.addListener("bounds_changed", this.onMapBoundChange.bind(this));
    map.addListener("zoom_changed", this.onZoomChanged.bind(this));
    map.addListener("dragstart", this.onMapDragStart.bind(this));
    map.addListener("dragend", this.onMapDragEnd.bind(this));

    this.mapEl?.addEventListener("keydown", (event) => {
      if (event.key === "Tab") {
        this.onMapFocus();
      }
    });

    this.map = map;

    if (this.input?.waitingForService) return;

    // If no zipcode in URL, get user location and load data
    if (getZipCode()) {
      try {
        await this.showUserLocation();
        this.setUserZipCode();
      } catch (error) {
        console.error(`User location error:${error}`);
      }
    } else {
      await this.loadMapForUserZipcode();
    }

    try {
      await this.getWatchListPOIs();
    } catch (error) {
      console.error(`Error fetching watchlist items:${error}`);
    } finally {
      map.addListener("idle", this.onMapBoundChangeDebounced.bind(this));
    }
  }

  initSetListingsHandler() {
    onMessage(MessageName.SetListings, async (event: MessageEvent) => {
      try {
        if ((event.data?.listings || []).length > 0) {
          await this.setListings(event.data.listings);
        }
      } catch {
        this.state.showErrorMessage = true;
      } finally {
        this.state.isLoading = false;
        this.state.isLoadingMoreItems = false;
      }
    });
  }

  initOpenLocationChangeDialogHandler() {
    onMessage(MessageName.OpenLocationChangeDialog, () => {
      this.onDialogOpen(MapDialog.LocationChange);
    });
  }

  async setUserZipCode() {
    if (this.state.userLocationCoordinates) {
      const userZipcode = await getZipCodeFromLatLng(this.input.apiKey, this.state.userLocationCoordinates);
      this.state.userLocationZipcode = userZipcode;
    }
  }

  onMapDragStart() {
    this.mapEl?.classList.add("map-dragging");
    if (this.userLocationMarker) {
      toggleWalkingDistanceTourtip(this.userLocationMarker, false);
    }

    this.state.showErrorMessage = false;
    this.state.shouldTriggerLoadMore = true;
  }

  onMapDragEnd() {
    this.mapEl?.classList.remove("map-dragging");
  }

  async setListings(listings: RequestedListing[]) {
    try {
      const [listingsResult] = await Promise.all([
        loadListings({
          listings: listings,
          csrfToken: this.input.csrfToken,
        }),
        this.map || this.initMap(),
      ]);
      // Workaround: XS returns no model (POI, help) if request query doesn't contain `listings` or `sources`
      sendModelToParentWindow(listingsResult);
      this.onLoadMoreSuccessHandler({ model: listingsResult, method: "post" });
    } catch {
      const fallbackModel = {
        mapview: {
          title: { textSpans: [{ text: t("mapview/map", "errorTitle") }] },
        },
      };
      sendModelToParentWindow(fallbackModel);
    }
  }

  addMarkersAndClusters(isLoadMore?: boolean, skipAutoCenter?: boolean) {
    const { minLatitude, maxLatitude } = calculateMinMaxLatitude(this.state.pointsOfInterest);
    this.minLatitude = minLatitude;
    this.maxLatitude = maxLatitude;

    this.addMarkers(isLoadMore, skipAutoCenter);
    this.addClusters();
  }

  closeMapHelp() {
    this.state.shouldOpenMapHelp = false;
  }

  clearAllFilters() {
    this.state.availableFilters = resetFilters(this.state.availableFilters);
    this.state.filteredPointsOfInterest = [];
  }

  async onUserLocationCenter(forceCenter = false) {
    this.isProgramaticallyChangingBounds = true;
    this.state.isAutoLoadTrackerVisible = false;
    this.state.shouldTriggerLoadMore = false;
    this.clearAllFilters();
    centerUserLocation({
      isLoadingMoreItems: this.state.isLoadingMoreItems,
      userLocationMarker: this.userLocationMarker,
      walkingDistanceBounds: this.walkingDistanceBounds,
      userLocationCoordinates: this.state.userLocationCoordinates,
      map: this.map,
      closeMapHelp: this.closeMapHelp.bind(this),
      hyperLocalItems: this.hyperLocalItems,
      isMobile: this.input.isMobile,
      forceCenter,
    });

    // Force center is set true only when the user clicks on the "Locate Me" button
    if (forceCenter) {
      await this.onLoadMoreHandler();
    }
  }

  async showUserLocation() {
    if (!navigator.geolocation) {
      this.state.showLocateMeButton = false;
      console.error("Geolocation is not supported by this browser.");
      closeMapHelpTooltip();
      return;
    }

    if (window.isSecureContext) {
      return this.getAndRenderUserLocation();
    }
  }

  async getAndRenderUserLocation() {
    return new Promise<void>((resolve, reject) => {
      const showPosition = (position: GeolocationPosition) => {
        if (!this.markerLibrary || !this.map) return;
        this.state.userLocationCoordinates = new google.maps.LatLng(position.coords.latitude, position.coords.longitude);
        this.userLocationMarker = renderUserLocationMarker(
          this.map,
          this.markerLibrary,
          this.state.userLocationCoordinates,
          this.state.walkingDistance,
        );
        // Empty click listener to make keyboard accessible
        this.userLocationMarker.marker.addListener("click", () => {});
        const distanceCircle = new google.maps.Circle({
          center: this.state.userLocationCoordinates,
          radius: this.state.walkingDistance?.distanceInMeters,
        });
        this.walkingDistanceBounds = distanceCircle.getBounds();
        resolve();
      };

      const errorHandler = (error: GeolocationPositionError) => {
        reject(new Error(`Geolocation error: code ${error.code} - ${error.message}`));
      };
      // SONAR: Geolocation is used to show the user's current position on the map
      // This enables centering the map on the user's location and displaying a location marker.
      navigator.geolocation.getCurrentPosition(showPosition.bind(this), errorHandler, { maximumAge: 60000, timeout: 10000 }); //NOSONAR
    });
  }

  drawWalkingDistanceOverlay() {
    if (!this.userLocationMarker || !this.map || !this.walkingDistanceBounds) return;

    const currentZoom = this.map.getZoom();
    const isRequiredZoomLevel = currentZoom && currentZoom >= SHOW_WALKING_DISTANCE_CIRCLE_ZOOM_LEVEL && currentZoom < SHOW_CIRCLES_ZOOM_LEVEL;

    if (!isRequiredZoomLevel) {
      updateWalkingDistanceOverlayRadius(this.userLocationMarker, 0);
      return;
    }

    const overlayProjection = this.walkingDistanceOverlay?.getProjection();

    if (overlayProjection) {
      const sw = overlayProjection.fromLatLngToContainerPixel(this.walkingDistanceBounds.getSouthWest())!;
      const ne = overlayProjection.fromLatLngToContainerPixel(this.walkingDistanceBounds.getNorthEast())!;

      updateWalkingDistanceOverlayRadius(this.userLocationMarker, ne.x - sw.x);
    }
  }

  showWalkingDistanceOverlay() {
    this.walkingDistanceOverlay = new google.maps.OverlayView();
    this.walkingDistanceOverlay.draw = () => this.drawWalkingDistanceOverlay();
    this.walkingDistanceOverlay.onRemove = () => {
      if (!this.userLocationMarker) return;
      updateWalkingDistanceOverlayRadius(this.userLocationMarker, 0);
    };
    this.walkingDistanceOverlay.setMap(this.map!);
  }

  hideWalkingDistanceOverlay() {
    if (!this.walkingDistanceOverlay) return;
    this.walkingDistanceOverlay.setMap(null);
  }

  onDestroy() {
    if (this.map) {
      google.maps.event.clearInstanceListeners(this.map);
    }
  }

  onMapClick() {
    this.state.isLeftRailItemHover = false;
    this.clearHighlightedMarkers();
    this.clearSelectedMarkers();

    if (this.userLocationMarker) {
      toggleWalkingDistanceTourtip(this.userLocationMarker, false);
    }
  }

  setSelectedMarkerCircle(selectedMarkerIndex: number) {
    const zoomLevel = this.map?.getZoom();
    if (zoomLevel && zoomLevel < SHOW_CIRCLES_ZOOM_LEVEL) return;
    if (typeof selectedMarkerIndex !== "number") return;
    const selectedMarkerCircle = this.points[selectedMarkerIndex];
    if (selectedMarkerCircle && !selectedMarkerCircle.circle) {
      const originalIndex = selectedMarkerCircle.originalIndex ?? selectedMarkerIndex;
      const point = this.state.pointsOfInterest?.[originalIndex];
      if (!point?.geometry?.circle) return;
      this.points[selectedMarkerIndex].circle = drawCircle(this.map!, point.geometry.circle);
    }
  }

  clearSelectedMarkerCircle(selectedMarkerIndex: number) {
    if (typeof selectedMarkerIndex !== "number") return;
    const selectedMarkerCircle = this.points?.[selectedMarkerIndex];
    if (selectedMarkerCircle?.circle) {
      this.points[selectedMarkerIndex].circle!.setMap(null);
      this.points[selectedMarkerIndex].circle = undefined;
    }
  }

  updateMarkerCircleWithZoomLevel(markerIndex: number | null) {
    const zoomLevel = this.map?.getZoom();
    const shouldShowMarkerCircle = zoomLevel && zoomLevel > SHOW_CIRCLES_ZOOM_LEVEL;
    if (markerIndex !== null && this.points[markerIndex]) {
      if (shouldShowMarkerCircle) {
        this.setSelectedMarkerCircle(markerIndex);
      } else {
        this.clearSelectedMarkerCircle(markerIndex);
      }
    }
  }

  clearAndAddSelectedMarkerWithPreview(markerIndex: number, cluster?: Cluster) {
    if (typeof markerIndex !== "number" || this.selectedMarkerWithPreview) return;

    const isStackedItems = this.state.selectedItems.length > 1 && cluster;
    const mapElement = document.querySelector("#map");
    const previewItemElement = document.querySelector(".preview-item");

    if (!mapElement || !previewItemElement) {
      console.error("Map or preview element not found");
      return;
    }

    if (isStackedItems) {
      this.createStackedMarkerWithPreview(cluster, mapElement, previewItemElement);
      if (this.selectedMarkerWithPreview) {
        (this.selectedMarkerWithPreview as MarkerWithCircle).marker.addListener("click", () => {});
        focusOnSelectedMarkerPreview(this.selectedMarkerWithPreview);
      }
    } else {
      this.setMarkerToSelected(markerIndex);
    }

    this.updateMarkerCircleWithZoomLevel(markerIndex);
  }

  createStackedMarkerWithPreview(cluster: Cluster, mapElement: Element, previewItemElement: Element) {
    const selectMarkersFromPoints = this.state.selectedItems
      .map((originalIdx) => this.points.find((point) => point.originalIndex === originalIdx)?.marker)
      .filter(Boolean) as google.maps.marker.AdvancedMarkerElement[];

    const markerElement = (<google.maps.marker.AdvancedMarkerElement>cluster.marker!).element;
    const previewCardPosition = getPreviewCardPosition(mapElement, markerElement, previewItemElement);
    const watchlistState = getWatchListItemState(
      this.state.watchListItems,
      this.state.pointsOfInterest[this.state.selectedItems[0]]?.itemCard?.card?.listingId,
    );

    const clusterItems = createClusterItems(cluster.markers, this.state.pointsOfInterest);
    const clusterPosition = positionToLiteral(cluster.position);

    this.selectedMarkerWithPreview = renderMarker({
      map: this.map!,
      markerLibrary: this.markerLibrary!,
      points: clusterItems,
      selectionState: "selected",
      watchlistState,
      isPreviewCardEnabled: this.state.isPreviewCardEnabled,
      handlePreviewClose: this.clearSelectedMarkers.bind(this),
      defaultPreviewPosition: previewCardPosition.horizontalPosition ? previewCardPosition : undefined,
      itemCardControls: this.state.itemCardControls,
      mapAccessibilityTexts: this.state.mapAccessibilityTexts,
      locality: this.state.locality,
      localizedContent: this.input.favoritesSettings?.localizedContent,
      watchInstance: this.watchInstance,
      watchListItems: this.state.watchListItems,
      handleWatchListItemToggle: this.handleWatchListItemToggle.bind(this),
      position: clusterPosition,
    });

    this.markerCluster?.removeMarkers(selectMarkersFromPoints);
  }

  updateLeftRailItems() {
    if (this.input.listingsView !== "left-rail" || this.input.isMobile) return;

    const bounds = this.map?.getBounds();
    if (!bounds) return;

    const updatedLeftRailItems = getVisiblePointsOfInterest(this.state.pointsOfInterest, this.points, bounds);
    const isLeftRailItemsChanged = this.state.leftRailItems.length > 0 && this.state.leftRailItems.length !== updatedLeftRailItems.length;
    this.state.leftRailItems = updatedLeftRailItems;

    if (isLeftRailItemsChanged) {
      this.state.isLeftRailItemHover = true;
    }
  }

  onMapFocus() {
    if (this.isFocused) return;
    this.isFocused = true;
    const currentBounds = this.map?.getBounds();
    if (currentBounds) {
      this.map?.fitBounds(currentBounds);
    }
  }

  async onMapBoundChangeDebounced() {
    if (this.state.shouldTriggerLoadMore && !this.state.isWatchlistPillActive) {
      await this.onLoadMoreHandler(true);
    }
    this.updateLeftRailItems();
    const visiblePointsOfInterest = getVisiblePointsOfInterest(this.state.pointsOfInterest, this.points, this.map?.getBounds());
    this.state.isEmptyMapTrackerVisible = !visiblePointsOfInterest || visiblePointsOfInterest.length === 0;
    if (this.isProgramaticallyChangingBounds) {
      this.isProgramaticallyChangingBounds = false;
    }
  }

  onDialogOpen(dialogType: MapDialog) {
    this.onDialogChange(dialogType, true);
  }

  onDialogClose(dialogType: MapDialog) {
    this.onDialogChange(dialogType, false);

    // When filter dialogs close, wait for the body scroll lock to be removed before triggering scroll
    if ([MapDialog.FilterCondition, MapDialog.FilterPrice].includes(dialogType)) {
      onFilterDialogStyleClear(() => {
        this.state.isLoading = false;
      });
    }
  }

  onDialogChange(dialogType: MapDialog, isOpen: boolean) {
    switch (dialogType) {
      case MapDialog.LocationChange:
        this.state.isLocationChangeDialogOpen = isOpen;
        break;
      case MapDialog.FilterCondition:
        this.state.isFilterConditionDialogOpen = isOpen;
        break;
      case MapDialog.FilterPrice:
        this.state.isFilterPriceDialogOpen = isOpen;
        break;
      default:
        break;
    }
  }

  onSubmitFilter(data: AvailableFilterOption[] | Partial<AvailableFilter>, filterKey: FilterKey) {
    if (filterKey === "condition") {
      const options = data as AvailableFilterOption[];
      const updatedFilters = updateFilter(this.state.availableFilters, filterKey, { options });
      this.state.availableFilters = updatedFilters;
    } else if (filterKey === "price") {
      const priceData = data as Partial<AvailableFilter>;
      const updatedFilters = updateFilter(this.state.availableFilters, filterKey, priceData);
      this.state.availableFilters = updatedFilters;
    }

    this.applyFilters();
  }

  applyFilters() {
    this.state.isLoading = true;
    this.state.filteredPointsOfInterest = getFilteredItems(this.state.pointsOfInterest, this.state.availableFilters);
    this.state.availableFilters = getAvailableFiltersToDisplay(this.state.pointsOfInterest, this.state.availableFilters);

    this.clearMap();
    this.addMarkersAndClusters(undefined, true);
  }

  onPostcodeInquiryClose() {
    window.location.href = "/";
  }

  async onLocationChangeDialogSubmit(data: LoadLocationDataParams) {
    if (this.state.showPostcodeInquiry) {
      const params = new URLSearchParams(window.location.search);
      params.set("zipcode", data.zipcode);
      params.set("distance", data.distance);
      window.location.href = `${window.location.pathname}?${params}`;
      return;
    }

    try {
      if (!this.map) {
        await this.initMap();
      }
      this.state.isAutoLoadTrackerVisible = false;
      this.state.isLoadingMoreItems = true;
      this.state.showErrorMessage = false;
      this.clearAllFilters();
      this.clearMap();
      const mapModules = await loadDataForLocation(data);
      this.state.isLoadingMoreItems = false;
      this.onLoadMoreSuccessHandler({ model: mapModules, method: "get" });
      if (!this.points.length) {
        const country = extractCountryCodeFromMarketplaceId(this.input.marketplaceId);
        const coordinates = await getLatLngFromAddress(this.input.apiKey, `${data.zipcode}, ${country}`);
        this.isProgramaticallyChangingBounds = true;
        this.map?.setCenter(coordinates);
        this.isProgramaticallyChangingBounds = false;
      }
    } catch (error) {
      console.error(`Error loading location data: ${error}`);
      this.state.isLoadingMoreItems = false;
    }
  }

  onMapBoundChange() {
    this.updateMarkerCircleWithZoomLevel(this.state.selectedItems[0]);
    this.updateMarkerCircleWithZoomLevel(this.state.highlightedItem);

    if (!this.currentBounds) {
      this.currentBounds = this.map?.getBounds();
      return;
    }

    this.currentBounds = this.map?.getBounds();
  }

  onZoomChanged() {
    if (this.isProgramaticallyChangingBounds) return;
    if (this.userLocationMarker) {
      toggleWalkingDistanceTourtip(this.userLocationMarker, false);
    }

    this.state.showErrorMessage = false;

    if (!this.currentBounds || !this.currentZoom) {
      this.currentZoom = this.map?.getZoom();
      return;
    }

    this.state.shouldTriggerLoadMore = true;

    if (this.state.highlightedItem) {
      this.clearHighlightedMarkers();
    }

    const isZoomed = this.map?.getZoom() !== this.currentZoom;
    if (this.state.isPreviewCardEnabled && this.state.selectedItems.length === 1 && isZoomed) {
      this.clearAndAddSelectedMarkerWithPreview(this.state.selectedItems[0]);
    }
  }

  addMarkers(isLoadMore?: boolean, skipAutoCenter?: boolean) {
    const pointsToRender = hasActiveFilters(this.state.availableFilters) ? this.state.filteredPointsOfInterest : this.state.pointsOfInterest;

    if (!pointsToRender || pointsToRender.length === 0) return;

    this.hyperLocalItems = [];
    this.points = pointsToRender
      .map((point, index) => {
        const originalIndex = hasActiveFilters(this.state.availableFilters)
          ? this.state.pointsOfInterest.findIndex((poi) => poi.itemCard?.card?.listingId === point.itemCard?.card?.listingId)
          : index;

        if (originalIndex === -1) return;

        const watchlistState = getWatchListItemState(this.state.watchListItems, point?.itemCard?.card?.listingId);
        const { marker, component } = renderMarker({
          map: this.map!,
          markerLibrary: this.markerLibrary!,
          points: [point],
          selectionState: "unselected",
          watchlistState,
          isPreviewCardEnabled: this.state.isPreviewCardEnabled,
          handlePreviewClose: this.clearSelectedMarkers.bind(this),
          itemCardControls: this.state.itemCardControls,
          mapAccessibilityTexts: this.state.mapAccessibilityTexts,
          locality: this.state.locality,
          localizedContent: this.input.favoritesSettings?.localizedContent,
          watchInstance: this.watchInstance,
          watchListItems: this.state.watchListItems,
          handleWatchListItemToggle: this.handleWatchListItemToggle.bind(this),
        });

        if (!marker || !component) return;

        const trackingList = point.trackingList;
        const isHyperLocal = this.walkingDistanceBounds?.contains(extractPosition(point.geometry));

        if (isHyperLocal) {
          this.hyperLocalItems.push(originalIndex);
        }

        if (trackingList?.[0].eventProperty && this.state.walkingDistance) {
          trackingList[0].eventProperty.isHyperLocal = isHyperLocal ? "true" : "false";
        }

        marker.id = originalIndex.toString();
        setAttrsForClickTracking(marker.element, trackingList);
        marker.addListener("click", () => this.onMarkerClick(index));
        return { marker, component, originalIndex };
      })
      .filter(Boolean) as MarkerWithCircle[];

    this.isProgramaticallyChangingBounds = true;

    const hasItemsInWalkingDistance = this.hyperLocalItems.length > 0;

    if (hasItemsInWalkingDistance) {
      this.showWalkingDistanceOverlay();
    } else {
      this.hideWalkingDistanceOverlay();
    }

    if (!skipAutoCenter) {
      const shouldFitAllItems = !isLoadMore || !this.state.isLoadingMoreItems;
      if (this.points.length && shouldFitAllItems) {
        closeMapHelpTooltip();
        this.map?.fitBounds(getMarkersBounds(this.points));
        if (isDistanceInHyperLocalRange()) {
          this.map?.setZoom(Math.min(this.map.getZoom()!, SHOW_WALKING_DISTANCE_CIRCLE_ZOOM_LEVEL));
        }
      }
    }
    this.isProgramaticallyChangingBounds = false;
  }

  addExistingMarker() {
    if (this.state.selectedItems.length > 0) {
      const originalIndex = this.state.selectedItems[0];
      const watchlistState = getWatchListItemState(this.state.watchListItems, this.state.pointsOfInterest[originalIndex]?.itemCard?.card?.listingId);
      this.clearSelectedMarkerCircle(originalIndex);
      const oldMarker = renderMarker({
        map: this.map!,
        markerLibrary: this.markerLibrary!,
        points: [this.state.pointsOfInterest[originalIndex]],
        itemCardControls: this.state.itemCardControls,
        selectionState: "unselected",
        watchlistState,
        defaultPreviewPosition: undefined,
        isPreviewCardEnabled: this.state.isPreviewCardEnabled,
        handlePreviewClose: this.clearSelectedMarkers.bind(this),
        mapAccessibilityTexts: this.state.mapAccessibilityTexts,
        locality: this.state.locality,
        localizedContent: this.input.favoritesSettings?.localizedContent,
        watchInstance: this.watchInstance,
        watchListItems: this.state.watchListItems,
        handleWatchListItemToggle: this.handleWatchListItemToggle.bind(this),
      });

      const markerIndex = this.points.findIndex((point) => (point.originalIndex ?? this.points.indexOf(point)) === originalIndex);

      oldMarker.marker.addListener("click", () => this.onMarkerClick(markerIndex));
      oldMarker.marker.id = originalIndex.toString();
      this.markerCluster?.addMarker(oldMarker.marker);
      this.points[markerIndex] = { ...oldMarker, originalIndex };
      // To re-run clustering algorithm after adding marker back to cluster
      this.addClusters();
    }
  }
  addExistingCluster() {
    if (this.state.selectedItems.length > 1 && this.selectedCluster?.markers) {
      this.markerCluster?.addMarkers(this.selectedCluster.markers);
    }
  }

  onMarkerClick(markerIndex: number) {
    if (this.state.isLoading || this.state.isLoadingMoreItems) return;
    this.state.isLeftRailItemHover = false;
    this.clearHighlightedMarkers();
    this.clearSelectedMarkers();

    if (this.state.isPreviewCardEnabled) {
      const selectedMarker = this.points[markerIndex]?.marker;
      const clusterGroup = this.markerCluster ? findClusterGroup(this.markerCluster, selectedMarker) : null;
      const hasMultipleItems = clusterGroup?.markers && clusterGroup.markers.length > 1;

      if (hasMultipleItems) {
        this.selectAllItemsInStacked(clusterGroup);
        this.clearAndAddSelectedMarkerWithPreview(markerIndex, clusterGroup);
      } else {
        this.clearAndAddSelectedMarkerWithPreview(markerIndex);
      }
    } else {
      this.setMarkerToSelected(markerIndex);
      this.updateMarkerCircleWithZoomLevel(markerIndex);
    }
  }

  onUnhoverItem() {
    if (this.scrollToMarkerTimeout) {
      clearTimeout(this.scrollToMarkerTimeout);
      this.scrollToMarkerTimeout = null;
    }
    this.clearHighlightedMarkers();
  }

  onItemHover(itemIndex: number, listingId?: string) {
    this.state.isLeftRailItemHover = true;

    const hasFilters = hasActiveFilters(this.state.availableFilters);
    const { markerIndex, originalIndex } = findMarkerIndex({
      points: this.points,
      itemIndex,
      listingId,
      hasFilters,
    });

    if (markerIndex === -1 || this.state.selectedItems.includes(originalIndex)) return;

    this.handleClusterHighlight(markerIndex);
    this.setMarkerToHighlighted(markerIndex, originalIndex);
    this.scrollToMarkerIfNotVisible(markerIndex);
  }

  handleClusterHighlight(markerIndex: number): void {
    if (!this.markerCluster) return;

    const selectedMarker = this.points[markerIndex].marker;
    const clusterGroup = findClusterGroup(this.markerCluster, selectedMarker);

    if (clusterGroup?.markers && clusterGroup.markers.length > 1) {
      const isStackedMarker = areMarkersInSameCoordinates(clusterGroup);

      if (isStackedMarker) {
        const reorderedItems = this.getReorderedClusterItems(markerIndex, clusterGroup);
        this.toggleHighlightedCluster(clusterGroup, reorderedItems);
        this.state.clusterPointsOfInterest = reorderedItems;
      } else {
        this.showMarkerOverCluster(markerIndex, clusterGroup);
        this.toggleHighlightedCluster(clusterGroup);
        this.state.clusterPointsOfInterest = createClusterItems(clusterGroup.markers, this.state.pointsOfInterest) || [];
      }
    }
  }

  showMarkerOverCluster(markerIndex: number, clusterGroup: Cluster): void {
    if (this.highlightedMarkerFromCluster) return;

    const originalIndex = this.points[markerIndex]?.originalIndex ?? markerIndex;
    const pointOfInterest = this.state.pointsOfInterest[originalIndex];
    if (!pointOfInterest) return;

    const clusterPosition = clusterGroup.position;
    if (!clusterPosition) return;

    const normalizedPosition = positionToLiteral(clusterPosition);

    const watchlistState = getWatchListItemState(this.state.watchListItems, pointOfInterest?.itemCard?.card?.listingId);

    const pointAtClusterPosition: PointOfInterest = {
      ...pointOfInterest,
      geometry: {
        point: {
          latitude: normalizedPosition.lat,
          longitude: normalizedPosition.lng,
        },
      },
    };

    this.highlightedMarkerFromCluster = renderMarker({
      map: this.map!,
      markerLibrary: this.markerLibrary!,
      points: [pointAtClusterPosition],
      itemCardControls: this.state.itemCardControls,
      selectionState: "highlighted",
      watchlistState,
      defaultPreviewPosition: undefined,
      isPreviewCardEnabled: false,
      handlePreviewClose: this.clearSelectedMarkers.bind(this),
      mapAccessibilityTexts: this.state.mapAccessibilityTexts,
      locality: this.state.locality,
      localizedContent: this.input.favoritesSettings?.localizedContent,
      watchInstance: this.watchInstance,
      watchListItems: this.state.watchListItems,
      handleWatchListItemToggle: this.handleWatchListItemToggle.bind(this),
    });

    if ("zIndex" in this.highlightedMarkerFromCluster.marker) {
      this.highlightedMarkerFromCluster.marker.zIndex = ACTIVE_MARKER_Z_INDEX + 1;
    }
  }

  getReorderedClusterItems(markerIndex: number, clusterGroup: Cluster): PointOfInterest[] {
    const originalIndex = this.points[markerIndex]?.originalIndex ?? markerIndex;
    const clusterItems = createClusterItems(clusterGroup.markers, this.state.pointsOfInterest);
    const hoveredItem = this.state.pointsOfInterest[originalIndex];

    if (!hoveredItem) return clusterItems;

    const hoveredItemIndexInCluster = clusterItems.findIndex((item) => item.itemCard?.card?.listingId === hoveredItem.itemCard?.card?.listingId);

    if (hoveredItemIndexInCluster === -1) return clusterItems;

    return [
      clusterItems[hoveredItemIndexInCluster],
      ...clusterItems.slice(0, hoveredItemIndexInCluster),
      ...clusterItems.slice(hoveredItemIndexInCluster + 1),
    ];
  }

  clearHighlightedMarkerOverCluster(): void {
    if (!this.highlightedMarkerFromCluster) return;

    (this.highlightedMarkerFromCluster.marker as google.maps.Marker).setMap(null);
    this.highlightedMarkerFromCluster = null;
  }

  onShowAll() {
    this.clearSelectedMarkers();

    if (this.state.leftRailItems.length < this.state.pointsOfInterest.length) {
      this.state.leftRailItems = this.state.pointsOfInterest;

      if (this.points.length) {
        this.isProgramaticallyChangingBounds = true;
        this.map?.fitBounds(getMarkersBounds(this.points));
      }
    }
  }

  clearHighlightedMarkers() {
    this.clearHighlightedMarkerOverCluster();
    this.clearHighlightedCluster();
    unHighlightMarkers(this.points);

    this.state.prevHighlightedItem = this.state.highlightedItem;
    this.state.highlightedItem = null;
  }

  setMarkerToHighlighted(markerIndex: number, originalIndex?: number) {
    const itemIndex = originalIndex ?? this.points[markerIndex]?.originalIndex ?? markerIndex;
    const isMarkerHighlighted = this.state.highlightedItem === itemIndex;
    const isMarkerSelected = this.state.selectedItems.includes(itemIndex);
    if (isMarkerHighlighted || isMarkerSelected || this.state.isLoading) return;

    this.state.prevHighlightedItem = this.state.highlightedItem;
    this.state.highlightedItem = itemIndex;

    if (markerIndex === null) return;

    highlightMarker(this.points[markerIndex]);
    this.setSelectedMarkerCircle(markerIndex);
  }

  scrollToMarkerIfNotVisible(markerIndex: number) {
    if (!this.map || markerIndex === -1) return;

    const marker = this.points[markerIndex]?.marker as google.maps.marker.AdvancedMarkerElement;
    if (!marker) return;

    const positionToCheck = getPositionToCheck(marker, this.markerCluster);
    if (!positionToCheck) return;

    const currentBounds = this.map.getBounds();
    if (!currentBounds) return;

    const isMarkerVisible = currentBounds.contains(positionToCheck);
    if (isMarkerVisible) return;

    this.handleScrollToMarker(markerIndex);
  }

  handleScrollToMarker(markerIndex: number) {
    if (this.scrollToMarkerTimeout) {
      clearTimeout(this.scrollToMarkerTimeout);
    }

    this.scrollToMarkerTimeout = setTimeout(() => {
      scrollToMarker({
        map: this.map!,
        markerCluster: this.markerCluster,
        points: this.points,
        markerIndex,
        onComplete: () => this.recreateHighlightedMarkerAfterScroll(markerIndex),
        setIsProgramaticallyChangingBounds: (value: boolean) => {
          this.isProgramaticallyChangingBounds = value;
        },
      });
      this.scrollToMarkerTimeout = null;
    }, SCROLL_TO_MARKER_DEBOUNCE_MS);
  }

  recreateHighlightedMarkerAfterScroll(markerIndex: number): void {
    const marker = this.points[markerIndex]?.marker;
    if (!marker) return;

    if (!this.markerCluster) {
      highlightMarker(this.points[markerIndex]);
      this.setSelectedMarkerCircle(markerIndex);
      return;
    }

    const clusterGroup = findClusterGroup(this.markerCluster, marker);

    if (!clusterGroup?.markers || clusterGroup.markers.length <= 1) {
      highlightMarker(this.points[markerIndex]);
      this.setSelectedMarkerCircle(markerIndex);
      return;
    }

    const isStackedMarker = areMarkersInSameCoordinates(clusterGroup);

    if (isStackedMarker) {
      const reorderedItems = this.getReorderedClusterItems(markerIndex, clusterGroup);
      this.toggleHighlightedCluster(clusterGroup, reorderedItems);
      return;
    }

    if (!this.highlightedMarkerFromCluster) {
      this.showMarkerOverCluster(markerIndex, clusterGroup);
    }
  }

  clearSelectedMarkers() {
    if (this.selectedMarkerWithPreview) {
      this[this.selectedCluster ? "addExistingCluster" : "addExistingMarker"]();
    }
    if (this.userLocationMarker) {
      toggleWalkingDistanceTourtip(this.userLocationMarker, false);
    }
    this.clearUnclusteredItem();
    this.clearSelectedCluster();
    this.clearClusterCircle();

    this.state.prevSelectedItems = this.state.selectedItems;
    this.state.selectedItems = [];
    unselectMarkers(this.points);
  }

  clearUnclusteredItem() {
    if (!this.selectedMarkerWithPreview) return;

    (this.selectedMarkerWithPreview?.marker as google.maps.Marker).setMap(null);
    this.selectedMarkerWithPreview = null;
  }

  setMarkerToSelected(markerIndex: number) {
    const originalIndex = this.points[markerIndex]?.originalIndex ?? markerIndex;
    const isMarkerSelected = this.state.selectedItems.includes(originalIndex);
    if (isMarkerSelected || markerIndex === null) return;
    this.clearSelectedCluster();
    unselectMarkers(this.points);
    this.clearClusterCircle();

    this.state.prevSelectedItems = this.state.selectedItems;
    this.state.selectedItems = [originalIndex];
    selectMarker(this.points[markerIndex]);
    focusOnSelectedMarkerPreview(this.points[markerIndex]);
  }

  clearClusterCircle(): void {
    this.clearSelectedMarkerCircle(this.state.selectedItems[0]);
    if (this.state.clusterPointsOfInterest.length === 0) return;
    this.state.clusterPointsOfInterest = [];
  }

  setSelectedItems(itemIndex: number[] = []): void {
    this.state.prevSelectedItems = this.state.selectedItems;
    this.state.selectedItems = itemIndex;
  }

  clearHighlightedCluster(): void {
    if (!this.highlightedCluster) return;
    (<google.maps.marker.AdvancedMarkerElement>this.highlightedCluster.marker!).zIndex = MARKER_Z_INDEX;
    (<google.maps.marker.AdvancedMarkerElement>this.highlightedCluster.marker)!.content = createClusterComponent({
      count: this.highlightedCluster.count,
      isStackedItems: areMarkersInSameCoordinates(this.highlightedCluster),
      clusterPointsOfInterest: createClusterItems(this.highlightedCluster.markers, this.state.pointsOfInterest),
      selectedMarker: null,
      mapAccessibilityTexts: this.state.mapAccessibilityTexts,
      locality: this.state.locality,
      localizedContent: this.input.favoritesSettings?.localizedContent,
      watchInstance: this.watchInstance,
      watchListItems: this.state.watchListItems,
      handleWatchListItemToggle: this.handleWatchListItemToggle.bind(this),
    });
    this.highlightedCluster = null;
  }

  clearSelectedCluster(): void {
    if (!this.selectedCluster) return;
    (<google.maps.marker.AdvancedMarkerElement>this.selectedCluster.marker!).zIndex = MARKER_Z_INDEX;
    (<google.maps.marker.AdvancedMarkerElement>this.selectedCluster.marker)!.content = createClusterComponent({
      count: this.selectedCluster.count,
      isStackedItems: areMarkersInSameCoordinates(this.selectedCluster),
      clusterPointsOfInterest: this.state.isWatchlistPillActive
        ? getWatchlistClusterItems({
            clusterMarkers: this.selectedCluster.markers,
            pointsOfInterest: this.state.pointsOfInterest,
            watchListItems: this.state.watchListItems,
          })
        : createClusterItems(this.selectedCluster.markers, this.state.pointsOfInterest),
      selectedMarker: null,
      mapAccessibilityTexts: this.state.mapAccessibilityTexts,
      locality: this.state.locality,
      localizedContent: this.input.favoritesSettings?.localizedContent,
      watchInstance: this.watchInstance,
      watchListItems: this.state.watchListItems,
      handleWatchListItemToggle: this.handleWatchListItemToggle.bind(this),
    });
    this.selectedCluster = null;
  }

  addClusters() {
    if (this.points?.length === 0) return;

    if (this.markerCluster) {
      this.markerCluster.clearMarkers();
    }

    this.markerCluster = new MarkerClusterer({
      map: this.map,
      markers: this.points.map((point) => point.marker),
      renderer: {
        render: (cluster) => {
          const { count, position, bounds, markers } = cluster;
          // When preview card is enabled, we show a marker with the preview card open instead of a selected cluster
          const selectedCluster = this.state.isPreviewCardEnabled ? null : this.selectedCluster;
          const clusterState: MarkerSelectionState = selectedCluster ? "selected" : this.highlightedCluster ? "highlighted" : "unselected";
          const isStackedItems = areMarkersInSameCoordinates(cluster);
          const rendered = clusterRenderer({
            markerLibrary: this.markerLibrary!,
            count,
            position,
            bounds: bounds!,
            clusterState,
            itemCardControls: this.state.itemCardControls,
            selectedCluster,
            highlightedCluster: this.highlightedCluster,
            isStackedItems,
            clusterPointsOfInterest: createClusterItems(markers, this.state.pointsOfInterest),
            selectedMarker: this.points[this.state.selectedItems[0]],
            mapAccessibilityTexts: this.state.mapAccessibilityTexts,
            locality: this.state.locality,
            localizedContent: this.input.favoritesSettings?.localizedContent,
            watchInstance: this.watchInstance,
            watchListItems: this.state.watchListItems,
            handleWatchListItemToggle: this.handleWatchListItemToggle.bind(this),
          });
          const trackingData = isStackedItems ? trackingDataClusterStacked(this.input.entryPageId) : trackingDataCluster(this.input.entryPageId);
          setAttrsForClickTracking(rendered.element, [trackingData]);

          // click event listener added to cluster for keyboard accessibility
          rendered.addListener("click", () => {});

          return rendered;
        },
      },
      onClusterClick: this.onClusterClick.bind(this),
      algorithm: new SuperClusterAlgorithm({ maxZoom: MAX_ZOOM_LEVEL, radius: DEFAULT_CLUSTER_RADIUS, extent: DEFAULT_EXTENT }),
    });
  }

  selectAllItemsInStacked(cluster: Cluster) {
    this.state.isLeftRailItemHover = false;
    const selectedItemIds = cluster.markers?.map((marker) => parseInt((marker as any).id)).reverse() || [];
    this.setSelectedItems(selectedItemIds);
  }

  onClusterClick(_: google.maps.MapMouseEvent, cluster: Cluster) {
    if (!cluster.bounds || this.state.isLoading || this.state.isLoadingMoreItems) return;

    const clusterCircle = this.points?.[this.state.selectedItems[0]]?.circle;
    const clusterCircleBounds = clusterCircle?.getBounds();
    const isSameLocation = clusterCircleBounds && isSameBound(cluster.bounds, clusterCircleBounds);
    const isStackedItems = areMarkersInSameCoordinates(cluster);
    const isItemHighlightedSameLocation = isSameLocation && typeof this.state.highlightedItem === "number" && this.input.listingsView === "left-rail";

    if (this.userLocationMarker) {
      toggleWalkingDistanceTourtip(this.userLocationMarker, false);
    }

    if (!isStackedItems && !isSameLocation) {
      moveMapToBounds(this.map!, cluster.bounds, this.mapEl!, DEFAULT_MAP_PADDING);
    }

    if (isStackedItems || isItemHighlightedSameLocation) {
      this.clearSelectedMarkers();
      this.clearHighlightedMarkers();
      this.selectAllItemsInStacked(cluster);
      this.toggleSelectedCluster(cluster);
      this.state.clusterPointsOfInterest = createClusterItems(cluster.markers, this.state.pointsOfInterest) || [];
    }
  }

  toggleHighlightedCluster(cluster: Cluster, clusterPointsOfInterest?: PointOfInterest[]) {
    if (this.highlightedCluster) {
      (<google.maps.marker.AdvancedMarkerElement>this.highlightedCluster.marker!).zIndex = ACTIVE_MARKER_Z_INDEX;
      (<google.maps.marker.AdvancedMarkerElement>this.highlightedCluster.marker!).content = createClusterComponent({
        count: this.highlightedCluster.count,
        isStackedItems: areMarkersInSameCoordinates(this.highlightedCluster),
        clusterPointsOfInterest: createClusterItems(this.highlightedCluster.markers, this.state.pointsOfInterest),
        selectedMarker: null,
        mapAccessibilityTexts: this.state.mapAccessibilityTexts,
        locality: this.state.locality,
        localizedContent: this.input.favoritesSettings?.localizedContent,
        watchInstance: this.watchInstance,
        watchListItems: this.state.watchListItems,
        handleWatchListItemToggle: this.handleWatchListItemToggle.bind(this),
      });
    }
    (<google.maps.marker.AdvancedMarkerElement>cluster.marker!).zIndex = ACTIVE_MARKER_Z_INDEX;
    (<google.maps.marker.AdvancedMarkerElement>cluster.marker!).content = createActiveClusterComponent({
      count: cluster.count,
      selectionState: "highlighted",
      isStackedItems: areMarkersInSameCoordinates(cluster),
      clusterPointsOfInterest: clusterPointsOfInterest ?? createClusterItems(cluster.markers, this.state.pointsOfInterest),
      selectedMarker: null,
      mapAccessibilityTexts: this.state.mapAccessibilityTexts,
      locality: this.state.locality,
      localizedContent: this.input.favoritesSettings?.localizedContent,
      watchInstance: this.watchInstance,
      watchListItems: this.state.watchListItems,
      handleWatchListItemToggle: this.handleWatchListItemToggle.bind(this),
    });
    this.highlightedCluster = cluster;
  }

  toggleSelectedCluster(cluster: Cluster) {
    const firstMarkerInCluster = this.state.selectedItems[0];
    if (this.state.isPreviewCardEnabled) {
      this.clearAndAddSelectedMarkerWithPreview(firstMarkerInCluster, cluster);
    } else {
      (<google.maps.marker.AdvancedMarkerElement>cluster.marker!).zIndex = ACTIVE_MARKER_Z_INDEX;
      (<google.maps.marker.AdvancedMarkerElement>cluster.marker!).content = createActiveClusterComponent({
        count: cluster.count,
        selectionState: "selected",
        isStackedItems: areMarkersInSameCoordinates(cluster),
        clusterPointsOfInterest: this.state.isWatchlistPillActive
          ? getWatchlistClusterItems({
              clusterMarkers: cluster.markers,
              pointsOfInterest: this.state.pointsOfInterest,
              watchListItems: this.state.watchListItems,
            })
          : createClusterItems(cluster.markers, this.state.pointsOfInterest),
        itemCardControls: this.state.itemCardControls,
        selectedMarker: null,
        mapAccessibilityTexts: this.state.mapAccessibilityTexts,
        locality: this.state.locality,
        localizedContent: this.input.favoritesSettings?.localizedContent,
        watchInstance: this.watchInstance,
        watchListItems: this.state.watchListItems,
        handleWatchListItemToggle: this.handleWatchListItemToggle.bind(this),
      });
      this.setSelectedMarkerCircle(firstMarkerInCluster);
    }
    this.selectedCluster = cluster;
  }

  handleZoomOut() {
    const currentZoomLevel = this.map?.getZoom() || 0;
    if (currentZoomLevel > 0) {
      this.map?.setZoom(currentZoomLevel - 1);
    }
  }
  handleZoomIn() {
    const currentZoomLevel = this.map?.getZoom() || 0;
    if (currentZoomLevel < MAX_ZOOM_LEVEL) {
      this.map?.setZoom(currentZoomLevel + 1);
    }
  }

  clearMarkers() {
    for (const point of this.points) {
      (point.marker as any).setMap(null);
      point.component.destroy();
    }
    this.points = [];
  }

  clearMarkerClusters() {
    if (this.markerCluster) {
      this.markerCluster.clearMarkers();
      this.markerCluster.setMap(null);
      this.markerCluster = null;
    }
  }

  clearSelectedItems() {
    this.clearSelectedMarkers();
    this.clearHighlightedMarkers();
    this.clearClusterCircle();
    this.clearSelectedCluster();
    this.hideWalkingDistanceOverlay();
  }

  clearMap() {
    this.clearSelectedItems();
    this.clearMarkers();
    this.clearMarkerClusters();
  }

  handleLoadMoreError() {
    this.state.showErrorMessage = true;
    this.state.isLoading = false;
    this.state.isLoadingMoreItems = false;
  }

  async onLoadMoreHandler(isAutoload = false) {
    if (this.state.isLoadingMoreItems) return;
    const region = this.map!.getBounds();
    const center = region!.getCenter();
    try {
      const zipCode = await getZipCodeFromLatLng(this.input.apiKey, center, region);
      if (!zipCode) {
        this.handleLoadMoreError();
        return;
      }

      const currentDistance = calculateRadiusForVisibleRegion(region!);
      const previousDistance = getDistance();

      if (zipCode === getZipCode() && currentDistance === previousDistance && !this.state.isWatchlistPillActive) {
        this.state.shouldTriggerLoadMore = false;
        this.state.isLoading = false;
        this.state.isLoadingMoreItems = false;
        return;
      }
      this.state.isAutoLoadTrackerVisible = isAutoload && !this.isProgramaticallyChangingBounds;
      this.state.isLoading = true;
      this.state.isLoadingMoreItems = true;
      const currentVersion = ++this.loadMoreVersion;
      const modules = await loadMore(this.map!, this.input.apiKey);
      if (currentVersion !== this.loadMoreVersion) return;
      this.onLoadMoreSuccessHandler({ model: modules, method: "get" }, isAutoload);
    } catch (error: any) {
      this.handleLoadMoreError();
      console.error("Failed on loading more items", error);
    } finally {
      this.state.isLoading = false;
      this.state.isLoadingMoreItems = false;
    }
  }

  onLoadMoreSuccessHandler(options: { model?: MapViewModules; method?: "get" | "post" }, isAutoload = false) {
    const { model, method } = options;

    if (!model) return;

    if (isAutoload && this.state.isWatchlistPillActive) {
      return;
    } else {
      this.clearMap();
    }

    const newPointsOfInterest = model.mapview?.pointsOfInterest ?? [];
    const availableFilters = getAvailableFiltersToDisplay(newPointsOfInterest, this.state.availableFilters);

    this.setState({
      ...(stateFromModel(model) as Partial<this["state"]>),
      filteredPointsOfInterest: hasActiveFilters(this.state.availableFilters) ? getFilteredItems(newPointsOfInterest, availableFilters) : [],
      shouldTriggerLoadMore: false,
      availableFilters,
      isWatchlistPillActive: false,
    });

    const { postalCodePillData } = model?.mapDataControls ?? {};
    sendUpdatedLocationToParentWindow(postalCodePillData);

    const isLoadMore = method === "get";
    this.addMarkersAndClusters(isLoadMore);
  }

  clearWatchlistMarker(itemIndex: number) {
    const marker = this.points.find((point) => point.originalIndex === itemIndex);
    if (!marker) return;
    this.markerCluster?.removeMarker(marker.marker);
    if (this.state.selectedItems.length > 2) {
      this.setSelectedItems(this.state.selectedItems.filter((item) => item !== itemIndex));
    } else {
      (marker.marker as any).setMap(null);
      this.clearSelectedMarkers();
    }
  }

  handleWatchListItemToggle(itemId: string) {
    this.state.watchListItems = toggleWatchlistItems({
      itemId,
      pointsOfInterest: this.state.pointsOfInterest,
      points: this.points,
      watchListItems: this.state.watchListItems,
      isWatchlistPillActive: this.state.isWatchlistPillActive,
      clearMarker: this.clearWatchlistMarker.bind(this),
    });

    if (this.state.isWatchlistPillActive) {
      this.refreshWatchlistViewState();
    }
  }
}
